import { AnyAction, Dispatch, MiddlewareAPI } from 'redux';

import { MESSAGES } from '../../constants';
import {
  logChatbotConversationCreatedFromStartOver,
  logChatbotErrorMessageAppears,
  logChatbotSendMessage,
  logSearchResultsPresented,
  logSingleArticlePresented,
} from '../bi';
import { TRootState } from '../common/reducers';
import { generateAnswerSlice } from '../common/reducers/answerGeneration';
import { chatActions } from '../common/reducers/chat';
import { errorsSlice } from '../common/reducers/errors';
import { fieldActions } from '../common/reducers/field';
import { ResetReason } from '../common/reducers/types';
import {
  selectIsFromAnswerGeneration,
  selectIsStartOverFromAnswerGeneration,
} from '../common/selectors/chat';
import { selectSolutions } from '../common/selectors/solutions';
import { loadArticleById, loadArticleByUri } from '../common/thunks/articles.thunk';
import { loadAttaches } from '../common/thunks/attaches.thunk';
import { loadSolutions } from '../common/thunks/solutions.thunk';
import { fedopsLogger, renderModel } from '../util/renderModel';

const isEditor = renderModel.isEditor();
const isStudioEditor = renderModel.isStudioEditor();
const locale = renderModel.getLocale();

const AI_ANSWER_FULL_INTERACTION = 'chatbot-ai-answer-full';
const AI_ANSWER_FIRST_CHUNK_INTERACTION = 'chatbot-ai-answer-first-chunk';

export const biMiddleware = ({ getState, dispatch }: MiddlewareAPI<any, TRootState>) => {
  const isDisplayAnswerGen = !isEditor && !isStudioEditor && locale === 'en';
  return (next: Dispatch) => (action: AnyAction) => {
    if (loadSolutions.fulfilled.match(action)) {
      const state = getState();
      if (!isDisplayAnswerGen) {
        logSearchResultsPresented(state, {
          articles: action.payload,
        });
      }
    } else if (chatActions.updateSession.match(action)) {
      const state = getState();
      const {
        chat: { resetReason, conversationId },
      } = state;

      if (resetReason === ResetReason.StartOver && action.payload.conversationId !== conversationId) {
        const isStartOverFromAG = selectIsStartOverFromAnswerGeneration(state);
        if (!isStartOverFromAG) {
          logChatbotConversationCreatedFromStartOver(state, {
            destinationConversationId: action.payload.conversationId,
          });
        } else {
          dispatch(chatActions.setIsStartOverFromAnswerGeneration(false));
        }
      }
    } else if (errorsSlice.actions.addError.match(action) || loadAttaches.rejected.match(action)) {
      if (action.payload?.bi) {
        logChatbotErrorMessageAppears(getState(), action.payload.bi);
      }
    } else if (fieldActions.sendMessage.match(action)) {
      fedopsLogger.interactionStarted(AI_ANSWER_FULL_INTERACTION);
      fedopsLogger.interactionStarted(AI_ANSWER_FIRST_CHUNK_INTERACTION);
      const state = getState();
      const isFromAnswerGeneration = selectIsFromAnswerGeneration(state);
      if (!isFromAnswerGeneration) {
        logChatbotSendMessage(state, action.payload.bi);
      } else {
        dispatch(chatActions.setIsFromAnswerGeneration(false));
      }
    } else if (chatActions.updateStreamingChunk.match(action)) {
      const isFirstChunk = getState().chat.streamingMessage?.messageId !== action.payload.messageId;
      if (isFirstChunk) {
        fedopsLogger.interactionEnded(AI_ANSWER_FIRST_CHUNK_INTERACTION);
      }
    } else if (chatActions.addItem.match(action) && action.payload.type === MESSAGES.AI_ARTICLE_ANSWER) {
      const wasStreaming = getState().chat.streamingMessage?.messageId === action.payload.id;
      if (!wasStreaming) {
        fedopsLogger.interactionEnded(AI_ANSWER_FULL_INTERACTION);
      }
    } else if (loadArticleById.fulfilled.match(action) || loadArticleByUri.fulfilled.match(action)) {
      Promise.resolve().then(() => logSingleArticlePresented(getState(), action.payload));
    } else if (generateAnswerSlice.actions.updateStreamingAnswer.match(action)) {
      const state = getState();
      const solutions = selectSolutions(state) || [];
      logSearchResultsPresented(state, {
        articles: solutions,
      });
    } else if (generateAnswerSlice.actions.failStreamingAnswer.match(action)) {
      const state = getState();
      const solutions = selectSolutions(state) || [];
      logSearchResultsPresented(state, {
        articles: solutions,
      });
    }

    return next(action);
  };
};
